import { useState } from "react";
import { v1 as uuidv4 } from "uuid";

import { mergeClassNames } from "@bokio/utils/classes";

import type { ReactNode } from "react";

import * as styles from "./radioField.scss";

export const getNativeRadioButtonClassName = (itemDisabled: boolean | undefined): string => {
	return mergeClassNames(itemDisabled ? styles.checkmarkDisabled : styles.checkmarkEnabled, styles.nativeRadio);
};

export interface RadioFieldProps {
	/** Label displayed next to the radio */
	label?: ReactNode;
	name: string;
	value: string | number;
	/** Whether the radio is selected */
	checked?: boolean;
	/** Disables the radio input */
	disabled?: boolean;
	/** Callback fired when the radio is selected */
	onChange?: (value: string | number) => void;
	testId?: string;
	inputTestId?: string;
	/**
	 * @deprecated Should only be used for design system internal overrides.
	 */
	className?: string;
	/**
	 * @deprecated Should only be used for design system internal overrides.
	 */
	labelClassName?: string;
}

export const RadioField = (props: RadioFieldProps) => {
	const { label, disabled, className, labelClassName, name, value, checked, onChange, testId, inputTestId } = props;

	const [uniqueId] = useState(() => uuidv4());

	const handleChange = () => {
		if (onChange) {
			onChange(value);
		}
	};

	const renderInput = (additionalClassName?: string) => {
		return (
			<input
				id={uniqueId}
				type="radio"
				name={name}
				value={value}
				data-testid={inputTestId}
				onChange={handleChange}
				checked={!!checked}
				disabled={disabled}
				className={mergeClassNames(
					styles.nativeRadio,
					disabled ? styles.checkmarkDisabled : styles.checkmarkEnabled,
					additionalClassName,
				)}
			/>
		);
	};

	return label ? (
		<label
			htmlFor={uniqueId}
			data-testid={testId}
			data-value={value}
			data-name={name}
			className={mergeClassNames(styles.radio, disabled && styles.radioDisabled, className)}
		>
			{renderInput()}
			<span className={mergeClassNames(styles.radioLabel, labelClassName)}>{label}</span>
		</label>
	) : (
		renderInput(className)
	);
};
